/**
 * Caricamenti — «cos'è entrato, cosa no, e cos'è cambiato da ieri».
 *
 * Sezione nuova (14-09): prima il caricamento del piano era un dialogo e
 * nient'altro, e dell'esito si sapeva solo che era andato bene. La SPEC §4.2 la
 * chiama «il punto su cui si gioca la credibilità del modulo con Giacomo», ed è
 * esatto: chi carica un file vuole sapere cosa ne è stato di ogni riga, e se
 * non glielo dici smette di fidarsi dei numeri che vengono dopo.
 *
 * Tre cose, in quest'ordine, che sono le tre domande di chi ha appena caricato:
 *
 *  1. **com'è andata** — righe entrate, scartate, duplicate, segnalate;
 *  2. **cos'è cambiato** rispetto al caricamento precedente, col fabbisogno
 *     cresciuto tenuto separato da quello evaso;
 *  3. **lo storico**, per tornare su un caricamento di ieri.
 *
 * ⚠️ Ogni elenco dichiara il proprio totale accanto a quanti ne mostra. Non è
 * un vezzo: una lista tagliata in silenzio fa sembrare un problema più piccolo
 * di quello che è, ed è espressamente vietato dalla SPEC.
 */
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  CalendarClock,
  CheckCircle2,
  Copy,
  FileSpreadsheet,
  Info,
  Loader2,
  Upload,
  XCircle,
} from "lucide-react";
import { useState } from "react";

import { RigaTesta } from "@/components/RigaTesta";
import {
  useM2Caricamenti,
  useM2Confronto,
  useM2Referto,
  useM2RefertoCorrente,
} from "@/hooks/useM2";
import { cn } from "@/lib/utils";
import { fmtData, fmtDataOra, num } from "@/pages/M2/format";
import type {
  ConfrontoResponse,
  Elenco,
  FabbisognoRigaRead,
  MrpClasse,
  RefertoResponse,
  VoceConfronto,
} from "@/types/m2";

const ETICHETTA_CLASSE: Record<MrpClasse, string> = {
  prodotto_finito: "Prodotti finiti",
  semilavorato: "Semilavorati da estrudere",
  packaging: "Materiale di confezionamento",
  non_classificata: "Non classificate",
};

/** Come si chiama, in italiano, ciascuna spiegazione del confronto. */
const ETICHETTA_SPIEGAZIONE: Record<string, { label: string; tono: string }> = {
  cresciuto: { label: "fabbisogno cresciuto", tono: "text-amber-700 dark:text-amber-400" },
  evaso: { label: "evaso — prodotto", tono: "text-emerald-700 dark:text-emerald-400" },
  domanda_calata: { label: "domanda calata", tono: "text-muted-foreground" },
  non_verificabile: { label: "non verificabile", tono: "text-muted-foreground" },
};

export function CaricamentiM2({ onCarica }: { onCarica: (() => void) | null }) {
  const [scelto, setScelto] = useState<string | null>(null);
  const corrente = useM2RefertoCorrente();
  const storico = useM2Caricamenti();
  // Senza scelta esplicita si guarda il corrente: è quello che si è appena
  // caricato, ed è la ragione per cui si apre questa sezione.
  const idAttivo = scelto ?? corrente.data?.caricamento.id ?? null;
  const altro = useM2Referto(scelto);
  const referto = scelto ? altro.data : corrente.data;
  const confronto = useM2Confronto(idAttivo);

  if (corrente.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Caricamento…
      </div>
    );
  }

  if (!corrente.data) return <NessunFabbisogno onCarica={onCarica} />;

  return (
    <div className="space-y-4">
      <RigaTesta
        contesto={`${num(referto?.caricamento.n_righe_file ?? 0)} righe nel file`}
        caricatoIl={referto?.caricamento.caricato_il}
        etichettaData="fabbisogno caricato il"
        titoloBadge="Il fabbisogno viene da un export del gestionale: più invecchia, meno descrive le commesse di adesso"
      >
        {/* Il verso opposto del rimando che sta in FabbisognoM2: i due
            «fabbisogno» del modulo devono nominarsi a vicenda, o il primo che
            li confronta pensa che uno dei due sia sbagliato. */}
        <span className="text-[11.5px] text-muted-foreground">
          dall&apos;MRP del gestionale — la sezione <b>Fabbisogno</b> mostra invece quello
          esploso dalle commesse
        </span>
        {onCarica && (
          <button
            type="button"
            onClick={onCarica}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-primary hover:opacity-80"
          >
            <Upload className="size-3.5" />
            Carica il fabbisogno
          </button>
        )}
      </RigaTesta>

      {referto && <Referto referto={referto} />}
      {confronto.data && <Confronto dati={confronto.data} />}
      <Storico
        items={storico.data ?? []}
        attivo={idAttivo}
        onScegli={(id) => setScelto(id)}
      />
    </div>
  );
}

// ── 1. Com'è andata ──────────────────────────────────────────────────

function Referto({ referto }: { referto: RefertoResponse }) {
  const c = referto.caricamento;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Riquadro
          n={c.n_accettate}
          label="Righe entrate"
          icona={CheckCircle2}
          tono={c.n_accettate > 0 ? "ok" : "neutro"}
        />
        <Riquadro n={c.n_scartate} label="Scartate" icona={XCircle} tono="allarme" />
        <Riquadro n={c.n_duplicate} label="Duplicate" icona={Copy} tono="attenzione" />
        <Riquadro n={c.n_segnalate} label="Da guardare" icona={AlertTriangle} tono="attenzione" />
      </div>

      {/* Cosa c'era dentro. Il file mescola tre cose diverse e senza questa
          riga non si vede: era la prima sorpresa leggendo l'MRP vero. */}
      <div className="flex flex-wrap gap-2">
        {referto.per_classe.map((k) => (
          <div key={k.classe} className="rounded-lg border bg-card px-3 py-2">
            <div className="text-[13px] font-semibold tabular-nums">
              {num(k.righe)}{" "}
              <span className="text-[11px] font-normal text-muted-foreground">righe</span>
            </div>
            <div className="text-[11px] text-muted-foreground">
              {ETICHETTA_CLASSE[k.classe]}
              {k.kg ? ` · ${num(k.kg)} kg` : ""}
              {k.pezzi ? ` · ${num(k.pezzi)} pz` : ""}
            </div>
          </div>
        ))}
      </div>

      {referto.rilievi.length > 0 && (
        <section className="space-y-2">
          <Titolo>Da sapere su questo file</Titolo>
          {referto.rilievi.map((r) => (
            <div
              key={r.codice}
              className="flex gap-2.5 rounded-lg border bg-muted/50 px-3.5 py-3 text-[12.5px]"
            >
              <Info className="mt-0.5 size-4 shrink-0 text-primary" />
              <div>
                <div className="font-semibold">{r.titolo}</div>
                <div className="mt-0.5 text-muted-foreground">{r.dettaglio}</div>
              </div>
            </div>
          ))}
        </section>
      )}

      <TabellaRighe titolo="Righe scartate" elenco={referto.scartate} motivo />
      <TabellaRighe titolo="Righe duplicate" elenco={referto.duplicate} />
      <TabellaRighe titolo="Righe da guardare" elenco={referto.segnalate} />
    </div>
  );
}

function TabellaRighe({
  titolo,
  elenco,
  motivo,
}: {
  titolo: string;
  elenco: Elenco<FabbisognoRigaRead>;
  motivo?: boolean;
}) {
  if (elenco.totale === 0) return null;
  return (
    <section>
      <Titolo>
        {titolo}
        <Quante elenco={elenco} />
      </Titolo>
      <div className="overflow-hidden rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 text-left font-medium">Riga</th>
                <th className="px-3 py-2 text-left font-medium">Articolo</th>
                <th className="px-3 py-2 text-right font-medium">Quantità</th>
                <th className="px-3 py-2 text-left font-medium">Consegna</th>
                <th className="px-3 py-2 text-left font-medium">
                  {motivo ? "Perché è stata scartata" : "Cosa c'è da sapere"}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {elenco.items.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{r.riga_file}</td>
                  <td className="px-3 py-1.5">
                    <span className="font-mono text-[11.5px]">{r.codice_articolo ?? "—"}</span>
                    {r.descrizione && (
                      <span className="ml-1.5 text-[11px] text-muted-foreground">
                        {r.descrizione.slice(0, 40)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {r.quantita !== null ? num(r.quantita) : "—"}
                    <Unita riga={r} />
                  </td>
                  <td className="px-3 py-1.5 text-muted-foreground">
                    {fmtData(r.data_consegna)}
                  </td>
                  <td className="px-3 py-1.5 text-[11.5px] text-muted-foreground">
                    {motivo
                      ? r.motivo_scarto
                      : r.segnalazioni.map((s) => s.dettaglio).join(" · ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/**
 * L'unità di misura: quella dichiarata dal gestionale, e la freccia **solo**
 * quando l'abbiamo davvero reinterpretata.
 *
 * ⚠️ `NR → pz` non è una reinterpretazione: «numero» e «pezzi» sono la stessa
 * cosa scritta in due modi, e mettere la freccia su 58 righe su 251 svuota il
 * segnale proprio dove serve — sulle «Pal», che pezzi non vogliono dire affatto.
 * Visto guardando la pagina: la colonna era un campo di frecce tutte uguali.
 */
const SINONIMI: Record<string, string> = { nr: "pz", pz: "pz", kg: "kg" };

function Unita({ riga }: { riga: FabbisognoRigaRead }) {
  const dichiarata = riga.um_dichiarata ?? "";
  const usata = riga.um_normalizzata;
  const reinterpretata = Boolean(usata) && SINONIMI[dichiarata.toLowerCase()] !== usata;
  return (
    <span
      className={cn(
        "ml-1 text-[10.5px]",
        reinterpretata ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"
      )}
      title={
        reinterpretata
          ? `Il gestionale dichiara «${dichiarata}»; la quantità è stata letta come ${usata}.`
          : undefined
      }
    >
      {dichiarata}
      {reinterpretata ? ` → ${usata}` : ""}
    </span>
  );
}

// ── 2. Cos'è cambiato ────────────────────────────────────────────────

function Confronto({ dati }: { dati: ConfrontoResponse }) {
  if (!dati.confrontabile) {
    return (
      <div className="flex gap-2.5 rounded-lg border bg-muted/50 px-3.5 py-3 text-[12px] text-muted-foreground">
        <Info className="size-4 shrink-0" />
        <div>{dati.motivo}</div>
      </div>
    );
  }

  const gruppi: { titolo: string; elenco: Elenco<VoceConfronto> | null }[] = [
    { titolo: "Comparse", elenco: dati.comparse },
    { titolo: "Sparite", elenco: dati.sparite },
    { titolo: "Quantità cambiata", elenco: dati.quantita_cambiata },
    { titolo: "Data spostata", elenco: dati.data_cambiata },
  ];

  return (
    <section className="space-y-3">
      <Titolo>
        Cos&apos;è cambiato dal caricamento precedente
        <span className="ml-2 font-normal normal-case tracking-normal text-muted-foreground">
          del {fmtDataOra(dati.precedente_caricato_il)} · confronto per {dati.chiave}
        </span>
      </Titolo>

      {/* Quando lo stesso export è stato caricato più volte il confronto scavalca
          i doppioni, e va detto: altrimenti «del 16/09» sopra una lista di
          caricamenti che ne mostra due del 18/09 sembra un errore della pagina. */}
      {dati.saltati > 0 && (
        <div className="flex gap-2.5 rounded-lg border bg-muted/50 px-3.5 py-2.5 text-[12px] text-muted-foreground">
          <Info className="size-4 shrink-0" />
          <div>
            Nel mezzo {dati.saltati === 1 ? "c'è" : "ci sono"} <b>{dati.saltati}</b>{" "}
            {dati.saltati === 1 ? "caricamento identico" : "caricamenti identici"} a questo — stesse
            righe, stesse quantità, stesse date — {dati.saltati === 1 ? "saltato" : "saltati"} per
            non nascondere il cambiamento vero dietro un confronto a zero.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Riquadro n={dati.invariate} label="Invariate" tono="neutro" />
        <Riquadro
          n={(dati.comparse?.totale ?? 0) + (dati.quantita_cambiata?.totale ?? 0)}
          label="Cresciute o cambiate"
          icona={ArrowUpRight}
          tono="attenzione"
        />
        {/* ⚠️ I due numeri non si sommano mai: una riga calata perché è stata
            prodotta e una calata perché il cliente ha ridotto l'ordine sono
            l'opposto l'una dell'altra. Tenerli separati è §4.7. */}
        <Riquadro n={dati.evase} label="Evase (prodotte)" icona={CheckCircle2} tono="ok" />
        <Riquadro
          n={dati.domanda_calata}
          label="Domanda calata"
          icona={ArrowDownRight}
          tono="neutro"
        />
      </div>

      {dati.non_verificabili > 0 && (
        <div className="flex gap-2.5 rounded-lg border bg-muted/50 px-3.5 py-2.5 text-[12px] text-muted-foreground">
          <Info className="size-4 shrink-0" />
          <div>
            <b>{dati.non_verificabili}</b> righe di semilavorato sono calate e non si può dire
            perché: il semilavorato estruso non ha un&apos;etichetta a pallet, quindi da qui non
            si vede se è stato prodotto. Vale solo per il confezionamento.
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-card px-3.5 py-2.5 text-[12.5px]">
        Fabbisogno di estrusione: <b>{num(dati.kg_estrusione_prima)} kg</b> →{" "}
        <b>{num(dati.kg_estrusione_ora)} kg</b>
        <span className="ml-1 text-muted-foreground">
          ({dati.righe_prima} righe → {dati.righe_ora})
        </span>
      </div>

      {gruppi.map(
        ({ titolo, elenco }) =>
          elenco &&
          elenco.totale > 0 && (
            <div key={titolo}>
              <Titolo>
                {titolo}
                <Quante elenco={elenco} />
              </Titolo>
              <div className="overflow-hidden rounded-lg border bg-card">
                {elenco.items.map((v) => (
                  <VoceRiga key={v.riga_id} v={v} />
                ))}
              </div>
            </div>
          )
      )}
    </section>
  );
}

function VoceRiga({ v }: { v: VoceConfronto }) {
  const sp = v.spiegazione ? ETICHETTA_SPIEGAZIONE[v.spiegazione] : null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t px-3.5 py-2 text-[12.5px] first:border-t-0">
      <span className="font-mono text-[11.5px]">{v.codice_articolo ?? "—"}</span>
      {v.descrizione && (
        <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
          {v.descrizione}
        </span>
      )}
      {v.quantita_prima !== null && v.quantita_prima !== undefined ? (
        <span className="tabular-nums">
          {num(v.quantita_prima)} → <b>{num(v.quantita ?? 0)}</b> {v.um}
        </span>
      ) : (
        <span className="tabular-nums">
          {num(v.quantita ?? 0)} {v.um}
        </span>
      )}
      {v.data_prima && (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <CalendarClock className="size-3.5" />
          {fmtData(v.data_prima)} → {fmtData(v.data_consegna)}
        </span>
      )}
      {sp && <span className={cn("text-[11px] font-medium", sp.tono)}>{sp.label}</span>}
    </div>
  );
}

// ── 3. Lo storico ────────────────────────────────────────────────────

function Storico({
  items,
  attivo,
  onScegli,
}: {
  items: { id: string; filename_originale: string; caricato_il: string; n_righe_file: number; n_accettate: number; is_corrente: boolean; caricato_da_email: string | null }[];
  attivo: string | null;
  onScegli: (id: string) => void;
}) {
  if (items.length === 0) return null;
  return (
    <section>
      <Titolo>
        Caricamenti precedenti
        <span className="ml-2 font-normal normal-case tracking-normal text-muted-foreground">
          {items.length} conservati
        </span>
      </Titolo>
      <div className="overflow-hidden rounded-lg border bg-card">
        {items.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onScegli(c.id)}
            className={cn(
              "flex w-full flex-wrap items-center gap-x-3 gap-y-1 border-t px-3.5 py-2 text-left text-[12.5px] first:border-t-0 hover:bg-accent/40",
              attivo === c.id && "bg-accent/60"
            )}
          >
            <FileSpreadsheet className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="font-medium tabular-nums">{fmtDataOra(c.caricato_il)}</span>
            <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
              {c.filename_originale}
              {c.caricato_da_email ? ` · ${c.caricato_da_email}` : ""}
            </span>
            <span className="tabular-nums text-muted-foreground">
              {num(c.n_accettate)}/{num(c.n_righe_file)} righe
            </span>
            {c.is_corrente && (
              <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground">
                corrente
              </span>
            )}
          </button>
        ))}
      </div>
    </section>
  );
}

// ── pezzi comuni ─────────────────────────────────────────────────────

function Titolo({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {children}
    </h2>
  );
}

/** «mostrate 50 di 213»: la riga che impedisce a un elenco di mentire. */
function Quante({ elenco }: { elenco: Elenco<unknown> }) {
  if (elenco.mostrati >= elenco.totale) {
    return (
      <span className="ml-2 font-normal normal-case tracking-normal text-muted-foreground">
        {elenco.totale} in tutto
      </span>
    );
  }
  return (
    <span className="ml-2 font-normal normal-case tracking-normal text-amber-700 dark:text-amber-400">
      ne vedi {elenco.mostrati} di {elenco.totale}
    </span>
  );
}

const TONI: Record<string, string> = {
  ok: "text-emerald-700 dark:text-emerald-400",
  attenzione: "text-amber-700 dark:text-amber-400",
  allarme: "text-red-700 dark:text-red-400",
  neutro: "text-foreground",
};

function Riquadro({
  n,
  label,
  icona: Icona,
  tono = "neutro",
}: {
  n: number;
  label: string;
  icona?: React.ComponentType<{ className?: string }>;
  tono?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <div className="flex items-center gap-1.5">
        {Icona && <Icona className={cn("size-3.5", n > 0 ? TONI[tono] : "text-muted-foreground")} />}
        <span
          className={cn(
            "text-xl font-semibold leading-tight tabular-nums",
            n > 0 ? TONI[tono] : "text-muted-foreground"
          )}
        >
          {num(n)}
        </span>
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">{label}</div>
    </div>
  );
}

function NessunFabbisogno({ onCarica }: { onCarica: (() => void) | null }) {
  return (
    <div className="rounded-lg border border-dashed bg-muted/40 p-10 text-center">
      <p className="text-sm text-muted-foreground">
        Nessun fabbisogno caricato. Il piano si costruisce a partire dall&apos;MRP del gestionale.
      </p>
      {/* Chi non carica i file non deve leggere un invito a caricarli: per lui
          la frase qui sopra è già la risposta completa. */}
      {onCarica && (
        <button
          type="button"
          onClick={onCarica}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <Upload className="size-3.5" />
          Carica il fabbisogno
        </button>
      )}
    </div>
  );
}
